import { formatBytes, type RecordFileItem } from '@ofmos/shared/files';
import { type ReactNode, type RefObject, useRef, useState } from 'react';
import { Button, Dialog, useIsDesktop } from '../../ui';
import { FileRow } from './FileRow';
import { FileSheet } from './FileSheet';
import { FileSheetRow } from './FileSheetRow';

/**
 * THE ATTACHMENT LIST, AND THE OWNER OF EVERYTHING THAT OUTLIVES A ROW
 * (`docs/decisions/file-attachments.md` D18; wave 1c, finding M1).
 *
 * WHY THIS COMPONENT EXISTS AT ALL. A Remove confirmation owned by the row it removes
 * destroys its own opener. `Dialog` returns focus to the element that had it when it opened
 * and skips a detached node (`ui/Dialog/Dialog.tsx`, the rule at `docs/ios26-mechanisms.md`),
 * and on a phone it is worse than that: the sheet the confirmation lives in is UNMOUNTED with
 * the row rather than closed, so `onCloseAutoFocus` never runs at all. Wave 1b measured the
 * result on both widths - focus on `<body>`, polled every 120ms for 1.2s - and tried the two
 * fixes that leave the ownership alone. Neither held, for the same reason: nothing that a
 * removal deletes can be the thing that hands focus back.
 *
 * SO THE FIELD OWNS THE TWO SURVIVORS: which file the phone sheet is showing, and which file
 * the field is asking about. Both are keyed by `fileId`, both outlive the row, and the
 * removal therefore CLOSES them instead of deleting them. Focus then goes where a close
 * always sends it - to the opener if it is still connected, and to `returnFocusTo` when the
 * opener went away with its row.
 *
 * D18 IS UNCHANGED BY THE MOVE. The desktop row still carries Download and Remove; the phone
 * row is still one tap target whose sheet holds the actions; the destructive confirmation is
 * still INSIDE that sheet rather than in a row-level popover. Only the owner moved.
 */
export interface FileListProps {
  /** The list's accessible name: two FILES fields can be on one record page. */
  readonly label: string;
  readonly items: readonly RecordFileItem[];
  /** Absent means the list is read-only, and no row offers Remove. */
  readonly onRemove?: ((fileId: string) => void) | undefined;
  /**
   * The files whose record write has not settled yet (wave 1c, finding 3). Their bytes are on
   * the server and their LINK is not, so their previews wait rather than asking for a 404.
   */
  readonly unconfirmed?: ReadonlySet<string> | undefined;
  /**
   * The focusable the field keeps beside the list - its "Add files" button. It is what focus
   * returns to when the row that had it was the one just removed, which is the house rule for
   * a dropped focus (`RecordListFooter.tsx`: repair it, never take a live one).
   */
  readonly returnFocusTo?: RefObject<HTMLElement | null> | undefined;
  /** Trailing list items: the uploads in flight, which are not attachments yet. */
  readonly children?: ReactNode;
}

export function FileList({
  label,
  items,
  onRemove,
  unconfirmed,
  returnFocusTo,
  children,
}: FileListProps) {
  const isDesktop = useIsDesktop();
  const [selected, setSelected] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);

  /**
   * THE FILE A CLOSING OVERLAY IS STILL TALKING ABOUT.
   *
   * Radix keeps a dialog's content mounted through its exit animation, and the removal that
   * closes it takes the item out of `items` in the same commit - so reading the item from the
   * list would blank the title of the sheet that is announcing the removal. The last non-null
   * selection is remembered instead, which is also what keeps the sheet MOUNTED across the
   * removal: an overlay that unmounts cannot return focus.
   */
  const shownInSheet = useRef<RecordFileItem | null>(null);
  const shownInDialog = useRef<RecordFileItem | null>(null);
  const found = (fileId: string | null) =>
    fileId === null ? null : (items.find((item) => item.fileId === fileId) ?? null);

  const selectedItem = found(selected);
  if (selectedItem !== null) shownInSheet.current = selectedItem;
  const confirmingItem = found(confirming);
  if (confirmingItem !== null) shownInDialog.current = confirmingItem;

  const sheetItem = shownInSheet.current;
  const dialogItem = shownInDialog.current;
  const isPending = (item: RecordFileItem) => unconfirmed?.has(item.fileId) ?? false;

  /** Commit the removal, then CLOSE - in that order, and never by unmounting. */
  const remove = (fileId: string) => {
    onRemove?.(fileId);
    setConfirming(null);
    setSelected(null);
  };

  return (
    <>
      <ul aria-label={label} className="divide-y divide-line-subtle">
        {items.map((item) => (
          <li key={item.fileId}>
            {isDesktop ? (
              <FileRow
                item={item}
                pending={isPending(item)}
                {...(onRemove !== undefined ? { onRemove: () => setConfirming(item.fileId) } : {})}
              />
            ) : (
              <FileSheetRow
                item={item}
                onOpen={() => {
                  setConfirming(null);
                  setSelected(item.fileId);
                }}
              />
            )}
          </li>
        ))}
        {children}
      </ul>

      {/**
       * THE PHONE SHEET. Mounted from the first tap onwards and never again unmounted by a
       * removal, which is the whole of finding M1's fix on this width.
       */}
      {!isDesktop && sheetItem !== null && (
        <FileSheet
          item={sheetItem}
          open={selected !== null}
          onOpenChange={(next) => {
            if (!next) {
              setSelected(null);
              setConfirming(null);
            }
          }}
          confirming={confirming === sheetItem.fileId}
          onAskRemove={() => setConfirming(sheetItem.fileId)}
          onCancelRemove={() => setConfirming(null)}
          pending={isPending(sheetItem)}
          {...(onRemove !== undefined ? { onRemove: () => remove(sheetItem.fileId) } : {})}
          {...(returnFocusTo !== undefined ? { returnFocusTo } : {})}
        />
      )}

      {/**
       * THE DESKTOP CONFIRMATION, and section 12's sentence says exactly what happens: the
       * file is DELETED. There is no Detach-versus-Delete question here because D7 makes a
       * file belong to exactly one cell, ever - so one verb, and copy that does not lie.
       *
       * `variant="center"`: this branch only renders above the desktop breakpoint, where the
       * `auto` shape would resolve to the same centred dialog anyway. Below it, the
       * confirmation is a step inside the sheet (D18) rather than a second layer over it.
       */}
      {isDesktop && dialogItem !== null && (
        <Dialog
          open={confirming !== null}
          onOpenChange={(next) => {
            if (!next) setConfirming(null);
          }}
          title={`Remove ${dialogItem.label}?`}
          description="The file is deleted, and this cannot be undone."
          width="sm"
          variant="center"
          /**
           * THE SAME LIST RENDERS INSIDE THE TABLE'S CELL EDITOR (`FilesCellInput`), which is
           * a non-modal popover: without this, opening the confirmation closed the editor and
           * unmounted the confirmation with it, so Remove was unreachable in a cell at all
           * (wave 1c, finding 2). Harmless on the record page, where nothing asks.
           */
          nested
          {...(returnFocusTo !== undefined ? { returnFocusTo } : {})}
          footer={
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => setConfirming(null)}>
                Cancel
              </Button>
              <Button variant="danger" onClick={() => remove(dialogItem.fileId)}>
                Remove
              </Button>
            </div>
          }
        >
          <p className="text-sm text-ink-muted">{formatBytes(dialogItem.byteCount)}</p>
        </Dialog>
      )}
    </>
  );
}
